import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ModalBody,
  ModalFooter,
  ModalPrimaryButton,
  ModalSecondaryButton,
  ModalWrapper,
  ModalWrapperProps,
} from '@zooplus/ui-components';

import { handleBodyScrollOnModalShown } from '../../../utils/styleUtils';
import { OverlayIds, useOverlays } from '../../OverlaysProvider';

import styles from './BaseModal.module.scss';

interface ModalProps {
  modalId: OverlayIds;
  disabledSecondaryBtn?: boolean;
  disabledPrimaryBtn?: boolean;
  dataZta: string;
  dataZtaPrimaryBtn?: string;
  handleSubmit?: () => void;
  isLoading?: boolean;
  headingId: string;
  heading: string;
  size: ModalWrapperProps['size'];
  closeBtnAriaLabel: string;
  primaryButtonLabel?: string;
  isCloseOnBackdropClick?: boolean;
}

/**
 * Wraps the given children in a modal.
 */
const BaseModal: React.FC<ModalProps> = ({
  modalId,
  heading,
  headingId,
  size,
  handleSubmit,
  dataZta,
  dataZtaPrimaryBtn,
  closeBtnAriaLabel,
  primaryButtonLabel,
  isLoading = false,
  disabledPrimaryBtn = false,
  disabledSecondaryBtn = false,
  isCloseOnBackdropClick = false,
  children,
}) => {
  const { t } = useTranslation();
  const { checkIsActive, hideOverlay } = useOverlays();

  const closeModal = useCallback(() => {
    hideOverlay(modalId);
  }, [hideOverlay, modalId]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleSubmit?.();
  };

  const isActive = checkIsActive(modalId);

  useEffect(() => {
    // TODO : hack to be removed when it's handled properly by ui-components library
    handleBodyScrollOnModalShown(isActive);
  }, [isActive]);

  return isActive ? (
    <ModalWrapper
      size={size}
      isShown={isActive}
      onClose={closeModal}
      heading={heading}
      headingId={headingId}
      closeBtnAriaLabel={closeBtnAriaLabel}
      data-zta={dataZta}
      closeOnBackdropClick={isCloseOnBackdropClick}>
      <ModalBody>{isActive && children}</ModalBody>
      <ModalFooter>
        <ModalSecondaryButton
          className={styles.modalCancel}
          onClick={closeModal}
          disabled={disabledSecondaryBtn}
          data-zta="closeModalBtnBottom">
          {t('general.button.back')}
        </ModalSecondaryButton>

        {!!primaryButtonLabel && (
          <ModalPrimaryButton
            onClick={handleClick}
            disabled={disabledPrimaryBtn}
            isLoading={isLoading}
            loadingIconType="paw"
            data-zta={dataZtaPrimaryBtn ?? 'confirmModalBtnBottom'}>
            {primaryButtonLabel}
          </ModalPrimaryButton>
        )}
      </ModalFooter>
    </ModalWrapper>
  ) : null;
};

export default BaseModal;
